import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  inject,
  input,
  signal,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { MatButtonModule } from '@angular/material/button';
import { MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatListModule } from '@angular/material/list';
import { MatMenuTrigger } from '@angular/material/menu';
import { PuebloBase } from '@components/pueblo-base.component';
import { UserAvatar } from '@components/user-avatar/user-avatar.component';
import { ANON_USER } from '@http/store/user/user-state';
import {
  Pueblo,
  removeResourceNameFrom,
  User,
  UserResourceName,
} from 'pueblo-types';
import { firstValueFrom } from 'rxjs';

@Component({
  selector: 'pueblo-join-requests-dialog',
  templateUrl: 'pueblo-join-requests-dialog.component.html',
  styleUrl: 'pueblo-join-requests-dialog.component.scss',
  imports: [
    MatButtonModule,
    MatDialogModule,
    MatIconModule,
    MatListModule,
    UserAvatar,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PuebloJoinRequestsDialog extends PuebloBase {
  private readonly dialogRef = inject(MatDialogRef);

  readonly pueblo = input.required<Pueblo>();

  readonly $loadingUsers = signal<Set<UserResourceName>>(new Set());

  readonly $joinRequests = signal<User[]>([]);

  protected readonly $menuTrigger = signal<MatMenuTrigger | undefined>(
    undefined,
  );

  protected readonly $appUser = toSignal(this.httpStore.appUser$, {
    initialValue: ANON_USER,
  });

  protected readonly $isOwner = computed(() =>
    this.pueblo().owners.includes(this.$appUser().resourceName),
  );

  protected readonly indexByMember = new Map<UserResourceName, number>();

  // This, as opposed to putting the logic in the template, makes it so
  // the menu remains consistent until the next open (and doesn't flash
  // to different items while the closing animation plays).
  protected readonly $menuShowSetMember = signal<boolean>(false);

  constructor() {
    super();

    let firstLoad = true;
    effect(async () => {
      const joinRequests = [
        ...(await firstValueFrom(
          this.httpStore.users$([...this.pueblo().joinRequests]),
        )),
      ];

      if (joinRequests.length === 0) {
        this.dialogRef.close();
      }

      if (firstLoad) {
        for (let i = 0; i < joinRequests.length; i++) {
          this.indexByMember.set(joinRequests[i].resourceName, i);
        }
        firstLoad = false;
      }

      this.$joinRequests.set([
        ...joinRequests.sort(
          (a, b) =>
            (this.indexByMember.get(a.resourceName) ?? Infinity) -
            (this.indexByMember.get(b.resourceName) ?? Infinity),
        ),
      ]);
    });
  }

  protected async navigateToProfile(user: User): Promise<void> {
    await this.pbRouter.goToUser(user.resourceName);
    setTimeout(() => {
      this.dialogRef.close();
    }, 0);
  }

  protected handleMenuClick(user: User, event: Event) {
    event.stopPropagation();

    this.$menuShowSetMember.set(false);
  }

  protected setLoadingUser(user: User, loading: boolean): void {
    if (!loading) {
      this.$loadingUsers.set(
        new Set<UserResourceName>(
          removeResourceNameFrom(
            [...this.$loadingUsers(), user.resourceName],
            user.resourceName,
          ),
        ),
      );
      return;
    }
    this.$loadingUsers.set(
      new Set<UserResourceName>([...this.$loadingUsers(), user.resourceName]),
    );
  }

  protected rejectJoinRequest(joinRequest: User, event: Event): void {
    event.stopPropagation();
    this.setLoadingUser(joinRequest, true);
    this.httpStore.rejectJoinRequest(this.pueblo(), joinRequest.resourceName, {
      onSuccess: () => {
        this.$joinRequests.set(
          this.$joinRequests().filter(
            (user) => user.resourceName !== joinRequest.resourceName,
          ),
        );
        this.setLoadingUser(joinRequest, false);
      },
    });
  }

  protected acceptJoinRequest(joinRequest: User, event: Event): void {
    event.stopPropagation();
    this.setLoadingUser(joinRequest, true);
    this.httpStore.acceptJoinRequest(this.pueblo(), joinRequest.resourceName, {
      onSuccess: () => {
        this.$joinRequests.set(
          this.$joinRequests().filter(
            (user) => user.resourceName !== joinRequest.resourceName,
          ),
        );
        this.setLoadingUser(joinRequest, false);
      },
    });
  }
}
